import type {
  PoiImageHotspotData,
  PoiImageHotspotItem,
} from "@/scott/ui/blocks/poi-image-hotspot/poi-image-hotspot";
import { getBlockId } from "@/services/contentful/assemblies/page/adapter/utils";
import {
  UiConfigurationAdapter,
  type UiConfigurationInput,
} from "@/services/contentful/topics/UIConfiguration/ui-configuration.adapter";
import type { SectionHotspotPageBlock } from "@/types/page-block";
import { CONTENTFUL_DEFAULT_LOCALE } from "@/utils/locale-mapper";

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function optionalString(value: unknown): string | undefined {
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

/**
 * Resolves a translatable hotspot value for the requested locale. Values are
 * stored as locale-keyed maps (`{ en: "...", de: "..." }`); the requested
 * locale wins, falling back to the default locale, then to any first non-empty
 * value. Legacy entries store a plain string, which applies to every locale.
 */
function resolveLocalized(value: unknown, locale: string): string | undefined {
  if (typeof value === "string") {
    return optionalString(value);
  }

  if (!isObject(value)) {
    return;
  }

  return (
    optionalString(value[locale]) ??
    optionalString(value[CONTENTFUL_DEFAULT_LOCALE]) ??
    Object.values(value)
      .map(optionalString)
      .find((entry): entry is string => entry !== undefined)
  );
}

function parseHotspotItems(
  value: unknown,
  locale: string
): PoiImageHotspotData | undefined {
  if (!(isObject(value) && Array.isArray(value.hotspots))) {
    return;
  }

  const hotspots = value.hotspots
    .filter((item): item is Record<string, unknown> => isObject(item))
    .map((item): PoiImageHotspotItem | null => {
      const x = Number(item.x);
      const y = Number(item.y);
      const name = resolveLocalized(item.name, locale);

      if (!(Number.isFinite(x) && Number.isFinite(y) && name)) {
        return null;
      }

      const href = resolveLocalized(item.link, locale);

      return {
        x,
        y,
        name,
        description: resolveLocalized(item.description, locale),
        thumbnailUrl: optionalString(item.thumbnailUrl),
        thumbnailAlt: resolveLocalized(item.thumbnailAlt, locale),
        link: href ? { href } : undefined,
      };
    })
    .filter((item): item is PoiImageHotspotItem => item !== null);

  return { hotspots };
}

export interface SectionHotspotInput {
  sys?: { id: string };
  __typename: "Hotspot";
  internalName: string;
  imageUrl: string;
  hotspots?: unknown;
  fullWidth?: boolean;
  hotspotColor?: "blazeorange" | "darkteal" | "neonchartreuse" | null;
  uiConfiguration?: UiConfigurationInput;
}

export function SectionHotspotAdapter(
  item: SectionHotspotInput,
  index: number,
  locale: string = CONTENTFUL_DEFAULT_LOCALE
): SectionHotspotPageBlock {
  const internalName = item.internalName;
  const uiConfig = item.uiConfiguration
    ? UiConfigurationAdapter(item.uiConfiguration)
    : undefined;

  return {
    sys: item.sys,
    id: getBlockId(item, index),
    __typename: "SectionHotspot",
    internalName,
    imageUrl: item.imageUrl,
    imageAlt: internalName,
    hotspots: parseHotspotItems(item.hotspots, locale),
    width: item.fullWidth ? "full" : "contained",
    hotspotColor: item.hotspotColor ?? undefined,
    background: uiConfig?.backgroundColor,
    paddingTop: uiConfig?.paddingTop,
    paddingBottom: uiConfig?.paddingBottom,
  };
}
